import {
  Briefcase,
  Files,
  MagnifyingGlass,
  ShieldCheck,
  SignOut,
  SquaresFour,
  UsersThree,
  CaretDown,
  WarningIcon,
} from '@phosphor-icons/react';
import { NavLink } from 'react-router-dom';
import {
  getSidebarNavItemsForAccess,
  isSidebarNavItemVisible,
  logoutSidebarNavItem,
  moduleSidebarNavItems,
  settingsSidebarNavItem,
} from '@/config/sidebar-modules';
import { useAccessControl } from '@/contexts/AccessContext';
import { useLogout } from '@/hooks/useLogout';
import LogoutOverlay from '@/components/LogoutOverlay';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { RoleCode } from '@/config/role-codes';
import './Sidebar.css';
import UserAvatar from '@/components/UserAvatar';
import { Can } from './Can';
import LogoIcon from '@/assets/icons/logo.svg?react';
import { cn } from '@/lib/utils';
import type { Icon } from '@phosphor-icons/react';
import { useState } from 'react';

const SIDEBAR_ICON_SIZE = 18;

const ICONS: Record<string, Icon> = {
  squaresFour: SquaresFour,
  shieldCheck: ShieldCheck,
  magnifyingGlass: MagnifyingGlass,
  files: Files,
  briefcase: Briefcase,
  usersThree: UsersThree,
  logout: SignOut,
  warning: WarningIcon,
};

/**
 * Renders both the outline and filled weight of the glyph; CSS toggles which one is
 * visible based on the ancestor `.sidebar-row--active` class (mirrors the M4/M1 design
 * handoff's regular↔fill icon swap on the active nav item).
 */
const SidebarNavIconGlyph = ({ name }: { name: string }) => {
  const Icon = ICONS[name];
  return (
    <>
      <Icon
        size={SIDEBAR_ICON_SIZE}
        weight="regular"
        aria-hidden
        className="sidebar-row__icon-svg sidebar-row__icon-svg--outline"
      />
      <Icon
        size={SIDEBAR_ICON_SIZE}
        weight="fill"
        aria-hidden
        className="sidebar-row__icon-svg sidebar-row__icon-svg--fill"
      />
    </>
  );
};

const Sidebar = () => {
  const {
    role,
    snapshot,
    isDevRoleOverride,
    isLoading,
    profile,
    userRoles,
    activeRoleCode,
    selectRole,
  } = useAccessControl();
  const items = getSidebarNavItemsForAccess({
    role,
    snapshot,
    useRoleFallback: isDevRoleOverride || isLoading,
  });
  const visibleModules = moduleSidebarNavItems.filter((item) =>
    isSidebarNavItemVisible(item, {
      role,
      snapshot,
      useRoleFallback: isDevRoleOverride || isLoading,
      featureFlags: profile?.featureFlags,
    })
  );
  const isSettingsVisible = isSidebarNavItemVisible(settingsSidebarNavItem, {
    role,
    snapshot,
    useRoleFallback: isDevRoleOverride || isLoading,
  });
  const { isLoggingOut, handleLogout } = useLogout();
  const [roleMenuOpen, setRoleMenuOpen] = useState(false);
  const showRoleDropdown = userRoles.length > 1;
  const activeRoleLabel =
    userRoles.find((userRole) => userRole.code === activeRoleCode)?.name ?? role;
  return (
    <aside className="h-screen sticky top-0 sidebar" aria-label="Application">
      <LogoutOverlay open={isLoggingOut} />
      <div className="h-16 px-4 flex items-center gap-2 sidebar__brand">
        <LogoIcon />
        <span className="sidebar__product">CodiSoc</span>
        <span className="sidebar__version">v{__APP_VERSION__}</span>
      </div>
      <div className="flex-1 flex flex-col gap-0.5 px-2 py-2 overflow-y-auto [scrollbar-width:none] hover:[scrollbar-width:thin]">
        <nav className="flex flex-col gap-0.5" aria-label="Modules">
          {items.map((item) => {
            const content = (
              <>
                <span className="sidebar-row__icon" aria-hidden>
                  <SidebarNavIconGlyph name={item.icon} />
                </span>
                <span className="sidebar-row__text">
                  <span className="sidebar-row__label">{item.label}</span>
                </span>
                {item.tag && (
                  <span className="sidebar-row__tag" title="Not in active scope">
                    {item.tag}
                  </span>
                )}
              </>
            );

            if (item.path === null) {
              return (
                <div
                  key={item.id}
                  className="sidebar-row sidebar-row--disabled"
                  aria-disabled="true"
                  title="Coming soon"
                >
                  {content}
                </div>
              );
            }

            return (
              <NavLink
                key={item.id}
                to={item.path}
                end={item.path === '/dashboard'}
                className={({ isActive }: { isActive: boolean }) =>
                  cn('sidebar-row', isActive && 'sidebar-row--active')
                }
              >
                {content}
              </NavLink>
            );
          })}
        </nav>

        {visibleModules.length > 0 && (
          <>
            <div className="sidebar__nav-sep" aria-hidden="true" />
            <nav className="flex flex-col gap-0.5" aria-label="Modules">
              {visibleModules.map((item) => {
                const content = (
                  <>
                    <span className="sidebar-row__icon" aria-hidden>
                      <SidebarNavIconGlyph name={item.icon} />
                    </span>
                    <span className="sidebar-row__text">
                      <span className="sidebar-row__label">{item.label}</span>
                    </span>
                    {item.tag ? (
                      <span className="sidebar-row__tag" title="Not in active scope">
                        {item.tag}
                      </span>
                    ) : null}
                  </>
                );
                return (
                  <NavLink
                    key={item.id}
                    to={item.path}
                    end={item.path === '/dashboard'}
                    className={({ isActive }: { isActive: boolean }) =>
                      isActive ? 'sidebar-row sidebar-row--active' : 'sidebar-row'
                    }
                  >
                    {content}
                  </NavLink>
                );
              })}
            </nav>
          </>
        )}

        {isSettingsVisible && (
          <Can permission="READ_ROLE_MATRIX">
            <div className="sidebar__nav-sep" aria-hidden="true" />
            <nav className="flex flex-col gap-0.5" aria-label="Settings">
              <NavLink
                to={settingsSidebarNavItem.path!}
                end={settingsSidebarNavItem.path === '/user-settings'}
                className={({ isActive }: { isActive: boolean }) =>
                  isActive ? 'sidebar-row sidebar-row--active' : 'sidebar-row'
                }
              >
                <span className="sidebar-row__icon" aria-hidden>
                  <SidebarNavIconGlyph name={settingsSidebarNavItem.icon} />
                </span>
                <span className="sidebar-row__text">
                  <span className="sidebar-row__label">{settingsSidebarNavItem.label}</span>
                </span>
              </NavLink>
            </nav>
          </Can>
        )}
      </div>
      <div className="px-2.5">
        <button
          type="button"
          className="sidebar-row sidebar-row--button"
          onClick={handleLogout}
          disabled={isLoggingOut}
          aria-busy={isLoggingOut}
        >
          <span className="sidebar-row__icon" aria-hidden>
            <SidebarNavIconGlyph name={logoutSidebarNavItem.icon} />
          </span>
          <span className="sidebar-row__text">
            <span className="sidebar-row__label" style={{ fontWeight: 500 }}>
              {logoutSidebarNavItem.label}
            </span>
          </span>
        </button>
      </div>
      <div className="sidebar__dev">
        <div className="sidebar__user-cell">
          <div className="relative shrink-0">
            <UserAvatar
              firstName={profile?.user?.firstName}
              lastName={profile?.user?.lastName}
              size="lg"
              variant="brand"
            />
            <span
              className="absolute bottom-0.5 right-0.5 block size-2 rounded-full border-2 border-white bg-green-500"
              aria-hidden="true"
            />
          </div>
          <div className="min-w-0">
            <div className="sidebar__user-name">
              {profile?.user?.firstName} {profile?.user?.lastName}
            </div>
            {showRoleDropdown ? (
              <div className="sidebar__role-row">
                <div className="sidebar__user-role">{activeRoleLabel}</div>
                <Popover open={roleMenuOpen} onOpenChange={setRoleMenuOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="sidebar__role-arrow"
                      aria-label="Switch role"
                      aria-expanded={roleMenuOpen}
                    >
                      <CaretDown size={12} strokeWidth={2.5} aria-hidden />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    side="top"
                    sideOffset={6}
                    className="sidebar__role-menu"
                  >
                    {userRoles.map((userRole) => (
                      <button
                        key={userRole.id}
                        type="button"
                        className={cn(
                          'sidebar__role-option',
                          userRole.code === activeRoleCode && 'sidebar__role-option--active'
                        )}
                        onClick={() => {
                          selectRole(userRole.code as RoleCode);
                          setRoleMenuOpen(false);
                        }}
                      >
                        {userRole.name}
                      </button>
                    ))}
                  </PopoverContent>
                </Popover>
              </div>
            ) : (
              <div className="sidebar__user-role">{role}</div>
            )}
          </div>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
